import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Scale, Loader2, AlertCircle, Info, TrendingUp, TrendingDown,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import FAQLink from '../faqs/FAQLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EVENT_LABELS = {
  bond_coupon: 'Bond coupon',
  tbill_maturity: 'T-bill matures',
  fd_maturity: 'FD matures',
  dividend: 'Dividend',
  distribution: 'Distribution',
  convertible_maturity: 'Convertible matures',
  safe_maturity: 'SAFE matures',
};

function fmt(v, cur) {
  if (v == null) return '—';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

function fmtCompact(v) {
  if (v == null) return '—';
  try {
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
  } catch {
    return `${v}`;
  }
}

function CurrencyChips({ map, formatter }) {
  const entries = Object.entries(map || {});
  if (entries.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([cur, v]) => (
        <span key={cur} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700">
          {formatter(cur, v)}
        </span>
      ))}
    </div>
  );
}

function TaxYearPositionCard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({ realised: false, expected: false, unrealised: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(`${BACKEND_URL}/api/tax-year-position/snapshot`, { headers: authHeaders() });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail || `Request failed (${resp.status})`);
        }
        const d = await resp.json();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (k) => setExpanded((s) => ({ ...s, [k]: !s[k] }));

  return (
    <Card className="portfolio-card" data-testid="tax-year-position-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-emerald-700 shrink-0" />
            <CardTitle className="text-base sm:text-xl font-serif">Current Tax Year Position</CardTitle>
            {data?.window && (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs" data-testid="tax-year-position-window">
                {data.window.from} → {data.window.to}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading current-year position…
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="tax-year-position-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        ) : !data ? null : (
          <>
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="tax-year-position-disclaimer">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
              <span>
                A reconciliation and planning lens, not advice. No tax-rate maths, no jurisdiction logic.
                Your accountant remains responsible for the actual filing.
              </span>
            </div>

            {/* Panel 1 — Realised so far */}
            <Panel
              testid="panel-realised"
              title="Realised so far"
              subtitle="Income events + sales already booked since the tax year started"
              count={(data.realised_so_far.income_event_count || 0) + (data.realised_so_far.realised_sale_count || 0)}
              isOpen={expanded.realised}
              onToggle={() => toggle('realised')}
              summary={(
                <CurrencyChips
                  map={data.realised_so_far.by_currency}
                  formatter={(cur, v) => `${cur} income ${fmtCompact(v.income_gross)} · G/L ${fmtCompact(v.realised_gain_loss)}`}
                />
              )}
            >
              {(data.realised_so_far.income_events || []).length === 0 && (data.realised_so_far.realised_sales || []).length === 0 ? (
                <p className="text-xs text-slate-500">Nothing realised so far in this tax year.</p>
              ) : (
                <>
                  {data.realised_so_far.income_events?.length > 0 && (
                    <div className="space-y-1.5" data-testid="panel-realised-income-list">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Income</div>
                      {data.realised_so_far.income_events.map((e, i) => (
                        <EventRow key={`i-${i}`} event={e} />
                      ))}
                    </div>
                  )}
                  {data.realised_so_far.realised_sales?.length > 0 && (
                    <div className="space-y-1.5 mt-3" data-testid="panel-realised-sales-list">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Realised sales</div>
                      {data.realised_so_far.realised_sales.map((s, i) => (
                        <SaleRow key={`s-${i}`} sale={s} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </Panel>

            {/* Panel 2 — Expected before year-end */}
            <Panel
              testid="panel-expected"
              title="Expected before year-end"
              subtitle="High-confidence projections only — coupons, maturities, and steady dividends"
              count={data.expected_before_year_end.event_count}
              isOpen={expanded.expected}
              onToggle={() => toggle('expected')}
              summary={(
                <CurrencyChips
                  map={data.expected_before_year_end.by_currency}
                  formatter={(cur, v) => `${cur} ${fmtCompact(v)}`}
                />
              )}
            >
              {(data.expected_before_year_end.events || []).length === 0 ? (
                <p className="text-xs text-slate-500">No high-confidence cashflows expected before the tax year closes.</p>
              ) : (
                <div className="space-y-1.5" data-testid="panel-expected-list">
                  {data.expected_before_year_end.events.map((e, i) => (
                    <EventRow key={`e-${i}`} event={e} />
                  ))}
                </div>
              )}
            </Panel>

            {/* Panel 3 — Unrealised gain/loss per holding */}
            <Panel
              testid="panel-unrealised"
              title="Unrealised gain / loss"
              subtitle="Per sale-eligible holding — what would crystallise if you sold today"
              count={data.unrealised_per_holding.holding_count}
              isOpen={expanded.unrealised}
              onToggle={() => toggle('unrealised')}
              summary={(
                <CurrencyChips
                  map={data.unrealised_per_holding.by_currency}
                  formatter={(cur, v) => `${cur} ${fmtCompact(v.unrealised_gain_loss)} (${v.count})`}
                />
              )}
            >
              {(data.unrealised_per_holding.rows || []).length === 0 ? (
                <p className="text-xs text-slate-500">No unrealised gains/losses — add a current value on each eligible holding to surface this panel.</p>
              ) : (
                <div className="space-y-1.5" data-testid="panel-unrealised-list">
                  {data.unrealised_per_holding.rows.map((r) => (
                    <UnrealisedRow key={r.investment_id} row={r} />
                  ))}
                </div>
              )}
            </Panel>
            <div className="mt-2">
              <FAQLink categoryKey="tax" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Panel({ testid, title, subtitle, count, isOpen, onToggle, summary, children }) {
  return (
    <div className="border border-slate-200 rounded-md mb-3 bg-white" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-md"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                {title}
                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-300">{count}</Badge>
              </div>
              <div className="text-[11px] text-slate-500">{subtitle}</div>
            </div>
          </div>
          <div className="shrink-0">{summary}</div>
        </div>
      </button>
      {isOpen && (
        <div className="px-3 pb-3 pt-1" data-testid={`${testid}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}

function EventRow({ event: e }) {
  return (
    <div className="text-xs flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-100 bg-slate-50/50">
      <div className="min-w-0">
        <span className="font-medium text-slate-900">{e.event_date}</span>
        {' · '}
        <span className="text-slate-700">{EVENT_LABELS[e.event_type] || e.event_type}</span>
        {' · '}
        <span className="font-medium">{e.investment_name || '—'}</span>
        {e.counterparty && <span className="text-slate-500"> · {e.counterparty}</span>}
      </div>
      <div className="font-medium text-slate-900">{fmt(e.gross_amount, e.currency)}</div>
    </div>
  );
}

function SaleRow({ sale: s }) {
  const gain = Number(s.realised_gain_loss || 0);
  return (
    <div className="text-xs flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-100 bg-slate-50/50">
      <div className="min-w-0">
        <span className="font-medium text-slate-900">{s.maturity_date}</span>
        {' · sold · '}
        <span className="font-medium">{s.investment_name || '—'}</span>
        {' · proceeds '}
        <span className="font-medium">{fmt(s.proceeds, s.currency)}</span>
      </div>
      <div className={`font-medium ${gain >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
        {gain >= 0 ? '+' : ''}{fmt(gain, s.currency)}
      </div>
    </div>
  );
}

function UnrealisedRow({ row: r }) {
  const positive = (r.unrealised_gain_loss || 0) >= 0;
  return (
    <div
      className="text-xs flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-slate-100 bg-slate-50/50"
      data-testid={`panel-unrealised-row-${r.investment_id}`}
    >
      <div className="min-w-0">
        <span className="font-medium text-slate-900">{r.investment_name || '—'}</span>
        <span className="text-slate-500">
          {' · cost '}{fmt(r.cost_basis, r.currency)}
          {' · current '}{fmt(r.current_value, r.currency)}
          {r.value_is_estimated && <span className="text-slate-400"> (est.)</span>}
        </span>
      </div>
      <div className={`flex items-center gap-1 font-medium ${positive ? 'text-emerald-700' : 'text-red-600'}`}>
        {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {positive ? '+' : ''}{fmt(r.unrealised_gain_loss, r.currency)}
      </div>
    </div>
  );
}

export default TaxYearPositionCard;
